import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ALL_SERVICES = ["restaurant", "bar", "pool", "banquets", "coffee_shop",
                      "mini_bar", "room_service", "spa", "kitchen"];

/**
 * HOTEL-SVC-1 · useHotelServices
 *
 * Fetches the current-hotel service toggle map and returns a `Set` of enabled
 * service codes plus the catalog + reload helper. A successful response is
 * authoritative for everyone. On error / unresolved hotel context we branch by
 * role: Super Admins fail OPEN (platform browsing with no hotel selected should
 * still show the full nav), while hotel-level users fail CLOSED so a service a
 * hotel switched OFF is never resurfaced in their sidebar.
 */
export function useHotelServices() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin"
    || (user?.permissions || []).includes("*");
  const [enabled, setEnabled] = useState(() => new Set());
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded,  setLoaded]  = useState(false);
  const [source,  setSource]  = useState("loading");

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/hotels/v1/services/catalog"),
        api.get("/hotels/v1/services", {
          params: { _ts: Date.now() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        }),
      ]);
      setCatalog(c.data?.services || []);
      const map = s.data?.services || {};
      setEnabled(new Set(Object.entries(map).filter(([, v]) => v?.enabled).map(([k]) => k)));
      setSource(s.data?.source || "stored");
      setLoaded(true);
    } catch {
      // A successful response above is always honoured (respects on/off toggles
      // for everyone). We only reach here on error / no hotel context:
      //   • Super Admin  → fail OPEN so they can browse the full app with no
      //     hotel selected (enforcement doesn't apply to platform browsing).
      //   • Hotel user   → fail CLOSED (keep last-known / empty) so a
      //     toggled-off service never reappears.
      if (isSuperAdmin) {
        setEnabled(new Set(ALL_SERVICES));
        setSource("sa_fail_open");
      } else {
        setSource("unresolved");
      }
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    load();
    // Real-time refresh: re-poll periodically, on tab focus, and whenever the
    // services editor broadcasts a change in this tab. This ensures a hotel
    // user's sidebar reflects Super-Admin toggle changes without a re-login.
    const interval = setInterval(load, 8000);
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onChanged = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("hocp:services-changed", onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("hocp:services-changed", onChanged);
    };
  }, [load]);

  return { enabled, catalog, loading, loaded, source, reload: load };
}
